import { z } from 'zod'
import { AUTO_BLOCK_TYPES, BLOCK_TYPES, HIGHLIGHT_ICONS } from './types'
import { MAX_BLOCKS } from './blocks'

const text = (max: number, message: string) => z.string().max(max, message).nullable()
const filled = (s: string | null | undefined) => (s ?? '').trim() !== ''

/** The same rules as the API (HomepageRequest), so most mistakes show before saving. */
export const blockSchema = z
  .object({
    id: z.string().min(1),
    type: z.enum(BLOCK_TYPES),
    title: text(120, 'Use at most 120 characters.'),
    visible: z.boolean(),
    body: text(5000, 'Use at most 5000 characters.'),
    items: z.array(z.string().max(200, 'Use at most 200 characters.')).max(30),
    highlights: z
      .array(
        z.object({
          title: z
            .string()
            .trim()
            .min(1, 'Give the highlight a title.')
            .max(80, 'Use at most 80 characters.'),
          text: text(300, 'Use at most 300 characters.'),
          icon: z.enum(HIGHLIGHT_ICONS),
        }),
      )
      .max(6),
    faq: z
      .array(
        z.object({
          question: z.string().trim().min(1, 'Write the question.').max(200, 'Use at most 200 characters.'),
          answer: z.string().trim().min(1, 'Write the answer.').max(2000, 'Use at most 2000 characters.'),
        }),
      )
      .max(30),
    images: z
      .array(
        z.object({
          fileId: z.number(),
          url: z.string().nullable(),
          caption: text(150, 'Use at most 150 characters.'),
        }),
      )
      .max(12),
    limit: z.number().int().min(1).max(6).nullable(),
    mapUrl: z
      .string()
      .max(500)
      .refine(
        (v) => v.trim() === '' || /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(v.trim()),
        'Enter a link starting with https://',
      )
      .nullable(),
  })
  .superRefine((b, ctx) => {
    const issue = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message })
    if (b.type === 'text' && !filled(b.body)) issue('body', 'Write the text of this block.')
    if (b.type === 'list' && !b.items.some(filled)) issue('items', 'Add at least one item.')
    if (b.type === 'highlights' && b.highlights.length < 3)
      issue('highlights', 'Add between 3 and 6 highlights.')
    if (b.type === 'faq' && b.faq.length === 0) issue('faq', 'Add at least one question.')
    if (b.type === 'gallery' && b.images.length === 0) issue('images', 'Add at least one picture.')
  })

export const homepageSchema = z
  .object({
    enabled: z.boolean(),
    heroTitle: z.string().max(120, 'Use at most 120 characters.'),
    heroSubtitle: z.string().max(300, 'Use at most 300 characters.'),
    cover: z.object({ fileId: z.number(), url: z.string().nullable() }).nullable(),
    accentColor: z
      .string()
      .refine((v) => v === '' || /^#[0-9a-f]{6}$/i.test(v), 'Use a colour such as #1d4ed8.'),
    blocks: z.array(blockSchema).max(MAX_BLOCKS, 'A homepage can have at most 20 blocks.'),
  })
  .superRefine((v, ctx) => {
    const seen = new Set<string>()
    v.blocks.forEach((b, i) => {
      if (!AUTO_BLOCK_TYPES.includes(b.type)) return
      if (seen.has(b.type))
        ctx.addIssue({
          code: 'custom',
          path: ['blocks', i, 'type'],
          message: 'This block can only appear once.',
        })
      seen.add(b.type)
    })
  })
